import { QueryClient, QueryClientProvider, useMutation, useQuery as useReactQuery } from 'react-query';
import axios from './dependency/axios';
import { API_URL, API_URL_KRAKEN, PATH } from './endpoints';
import toast from 'react-hot-toast';
import {
  AGREEMENT_PENDING,
  useAgreementPopupStore,
} from '../../components/organisms/AgreementGate/agreementPopupStore';
// import { decision } from 'global';

/**
 * This is an abstracted method over axios.
 * Just provide the path and base url will be taken up based on node environment automatically
 * @param {*} data  { path: '/login/'}
 * @returns
 */

let appInfo = null;

// Above zero while a react-query query function runs; see useQuery below.
let queryFnDepth = 0;

async function Call(data) {
  // Read before the first await: a query function calls Call synchronously.
  const startedByQuery = queryFnDepth > 0;
  const authToken = localStorage.getItem('token');
  const sessionId = localStorage.getItem('_tapartsid');
  const deviceId = localStorage.getItem('_tapartdid');

  let headers = {};
  if (data?.headers) {
    headers = { ...headers, ...(data?.headers || {}) };
    delete data.headers;
  }
  if (!data.noHeaders) {
    if (authToken) {
      headers = { ...headers, authorization: authToken };
    }
    if (sessionId) {
      headers = { ...headers, 'X-Tap-Session-Id': sessionId };
    }
    if (deviceId) {
      headers = { ...headers, 'X-Tap-Device-Id': deviceId };
    }
    // headers = { ...headers, 'x-platform-request': decision.platform };
    if (appInfo?.build) {
      headers = { ...headers, 'x-client-build-id': appInfo.build };
    }
  }

  const baseURL = data.path.includes('payment-link') ? API_URL_KRAKEN : API_URL;

  let option = {
    url: data.path,
    baseURL,
    headers,
    withCredentials: true,
    ...data,
  };
  try {
    const r = await axios(option);
    const responseSessionId = r.headers['X-Tap-Partner-Session-Id'];
    const responseDeviceId = r.headers['X-Tap-Partner-Device-Id'];
    if (responseSessionId) {
      localStorage.setItem('_tapartsid', responseSessionId);
    }
    if (responseDeviceId) {
      localStorage.setItem('_tapartdid', responseDeviceId);
    }
    return data.getOriginalResponse === true ? r : r.data;
  } catch (err) {
    // if (err?.response?.status === 403) {
    //   if (window) {
    //     // console.error('Forbidden');
    //     // localStorage.removeItem('token');
    //     // window.location.replace("https://leafround.com/deals")
    //   } else {
    //     console.error('Failed to redirect');
    //   }
    //   throw err;
    // }
    // Backend refuses everything but view-only screens until the partner agreement is e-signed.
    // Only an action the partner took opens the popup: not a GET, and not a query, since screens
    // load data (some with POST) on mount, on tab change and on window focus.
    const method = (option.method || 'get').toLowerCase();
    if (isAgreementPending(err) && method !== 'get' && !startedByQuery) {
      useAgreementPopupStore.getState().open();
    }
    if (err?.response?.status === 401 && !data?.disableRedirect) {
      if (window) {
        localStorage.removeItem('token');
        // Dismiss all existing toasts before showing new one
        toast.dismiss();
        toast.error('Session expired. Please login again.');

        // if (process.env.NODE_ENV !== 'development') {
        window.location.replace('/login');
        // }
      } else {
        console.error('Failed to redirect');
      }
    }
    throw err;
  }
}

const isAgreementPending = (err) =>
  err?.response?.status === 403 && err?.response?.data?.errorCode === AGREEMENT_PENDING;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      // Same as react-query's default (3 retries), except a refusal for a pending agreement:
      // retrying cannot succeed until the partner signs.
      retry: (failureCount, error) => !isAgreementPending(error) && failureCount < 3,
    },
  },
});

const markedAsQuery =
  (queryFn) =>
  (...args) => {
    queryFnDepth += 1;
    try {
      return queryFn(...args);
    } finally {
      queryFnDepth -= 1;
    }
  };

/**
 * react-query's useQuery, with every Call its query function makes marked as a background read.
 * Typed as the original so TypeScript callers keep their data types.
 * @type {typeof useReactQuery}
 */
const useQuery = (...args) => {
  if (typeof args[1] === 'function') {
    args[1] = markedAsQuery(args[1]);
  } else if (args[0] && typeof args[0] === 'object' && !Array.isArray(args[0]) && args[0].queryFn) {
    args[0] = { ...args[0], queryFn: markedAsQuery(args[0].queryFn) };
  } else if (args[1] && typeof args[1] === 'object' && args[1].queryFn) {
    args[1] = { ...args[1], queryFn: markedAsQuery(args[1].queryFn) };
  }
  return useReactQuery(...args);
};

const CustomQueryClientProvider = ({ children }) => {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export {
  Call,
  PATH,
  API_URL,
  API_URL_KRAKEN,
  CustomQueryClientProvider,
  useQuery,
  useMutation,
  queryClient,
};
